import { Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { AlgorithmEffects } from '../../../effects/algorithm.effects';
import { AlgorithmDetailEffects } from '../../../effects/algorithm-detail.effects';
import { AlgorithmCompatibilityInfo } from '../../../state/algorithm-detail.state';
import { Engine, StepTask } from '@telespot/domain';
import type { Language } from '@protocols/domain';
import { useLocalization } from '@shared/localization';
import { filter, map, startWith, take, tap } from 'rxjs/operators';
import { PipelineStepDetailEffects } from '../../../effects/pipeline-step-detail.effects';
import { PipelineStepCompatibilityInfo } from '../../../state/pipeline-step-detail.state';
import { combineLatest, Observable } from 'rxjs';
import { AlgorithmSwitcherState } from '../../../state/algorithm-switcher.state';
import type { CompatibilityState, SwitchAlgorithmData } from '../../../state/algorithm-switcher.state';

@Injectable()
export class SwitchAlgorithmFacade {
  constructor(
    private readonly algorithmEffects: AlgorithmEffects,
    private readonly algorithmDetailEffects: AlgorithmDetailEffects,
    private readonly pipelineStepDetailEffects: PipelineStepDetailEffects,
    private readonly algorithmSwitcherState: AlgorithmSwitcherState
  ) {
    this.wireUpFromData(this.algorithmSwitcherState.data$);
    this.clearAlgorithmDetailWhenSelectionCleared(this.algorithmSwitcherState.algorithmToSwitchToId$);
    this.checkCompatibility(
      combineLatest([this.algorithmDetailEffects.compatibilityInfo$, this.pipelineStepDetailEffects.compatibilityInfo$])
    );
  }

  // ============================================================================
  // Selectors — passthrough from State
  // ============================================================================

  public readonly data$ = this.algorithmSwitcherState.data$;
  public readonly algorithmToSwitchToId$ = this.algorithmSwitcherState.algorithmToSwitchToId$;
  public readonly initialSearchTerm$ = this.algorithmSwitcherState.initialSearchTerm$;
  public readonly deprecatedWarning$ = this.algorithmSwitcherState.usingDeprecated$;
  public readonly compatibilityState$ = this.algorithmSwitcherState.compatibilityState$;
  public readonly hintKey$ = this.algorithmSwitcherState.hintKey$;

  public readonly cancellation$ = this.algorithmSwitcherState.cancellation$.asObservable();
  public readonly acceptance$ = this.algorithmSwitcherState.acceptance$.asObservable();

  // ============================================================================
  // Selectors — passthrough from Effects
  // ============================================================================

  public readonly noAlgorithms$ = this.algorithmEffects.noItems$;
  public readonly loadingAlgorithms$ = this.algorithmEffects.loading$;

  // ============================================================================
  // Derived selectors
  // ============================================================================

  private readonly algorithms$ = this.algorithmEffects.items$;
  private readonly hasMoreItems$ = this.algorithmEffects.hasMoreItems$;

  public readonly currentLanguage$ = useLocalization().currentLanguage$;
  public readonly loaded$ = this.hasMoreItems$.pipe(map((hasMoreItems) => !hasMoreItems));

  public readonly algorithmOptions$ = this.algorithms$.pipe(
    map((algorithms) =>
      algorithms.map((algorithm) => ({
        id: algorithm.id,
        name: algorithm.name,
        description: algorithm.description,
        version: algorithm.version,
        regulated: algorithm.regulated,
        deprecated: algorithm.deprecated,
      }))
    )
  );

  public readonly selectedAlgorithm$ = combineLatest([this.algorithmOptions$, this.algorithmToSwitchToId$]).pipe(
    map(([options, id]) => options.find((option) => option.id === id) ?? null)
  );

  public readonly pipelineName$ = combineLatest([
    this.data$,
    this.currentLanguage$.pipe(startWith(useLocalization().currentLanguage)),
  ]).pipe(map(([data, language]) => (data ? this.resolvePipelineName(data, language) : '')));

  public readonly readyToSwitch$ = combineLatest([this.algorithmToSwitchToId$, this.data$]).pipe(
    map(([id, data]) => Boolean(id && data && id !== data.currentAlgorithmId))
  );

  public readonly compatibilityWarning$ = this.compatibilityState$.pipe(
    map((state) => {
      switch (state?.kind) {
        case 'no_ocr':
          return 'table.algorithms.switch_algorithm_dialog.no_ocr';
        case 'no_mapping':
          return 'table.algorithms.switch_algorithm_dialog.no_mapping';
        case 'label_mismatch':
          return 'table.algorithms.switch_algorithm_dialog.label_mismatch';
        default:
          return undefined;
      }
    })
  );

  /**
   * The labels the pipeline step requires but the selected algorithm does not
   * output, resolved to a display name in the current language. Empty unless
   * the compatibility state is a label mismatch. When a label name could not
   * be resolved, `displayName` equals the uuid.
   */
  public readonly mismatchedLabels$ = combineLatest([
    this.compatibilityState$,
    this.currentLanguage$.pipe(startWith(useLocalization().currentLanguage)),
  ]).pipe(
    map(([state, language]) =>
      state?.kind === 'label_mismatch'
        ? state.mismatchedLabels.map(({ uuid, name }) => ({
            uuid,
            displayName: name[language as Language] ?? name.en,
          }))
        : []
    )
  );

  public readonly algorithmListError$ = this.algorithmEffects.error$.pipe(
    map((error) => (error ? 'table.algorithms.switch_algorithm_dialog.errors.load_algorithms' : null))
  );

  public readonly compatibilityError$ = combineLatest([
    this.pipelineStepDetailEffects.error$,
    this.algorithmDetailEffects.error$,
  ]).pipe(map(([stepError, algorithmError]) => stepError ?? algorithmError ?? null));

  // ============================================================================
  // Updaters
  // ============================================================================

  public readonly switchToAlgorithm = this.algorithmSwitcherState.setAlgorithmToSwitchToId;

  // Delegated updaters from Effects
  public readonly setSearchTerm = this.algorithmEffects.setSearchTerm;
  public readonly setIncludeDeprecated = this.algorithmEffects.setIncludeDeprecated;

  // ============================================================================
  // Effects — defined on the injected State
  // ============================================================================

  public readonly checkCompatibility = this.algorithmSwitcherState.effect(
    (input$: Observable<[AlgorithmCompatibilityInfo | undefined, PipelineStepCompatibilityInfo | undefined]>) =>
      input$.pipe(
        tap(([algorithmCompatibilityInfo, stepCompatibilityInfo]) => {
          if (!algorithmCompatibilityInfo || !stepCompatibilityInfo) {
            this.algorithmSwitcherState.setUsingDeprecated(false);
            this.algorithmSwitcherState.setCompatibilityState(undefined);
          }
        }),
        filter(
          (pair): pair is [AlgorithmCompatibilityInfo, PipelineStepCompatibilityInfo] =>
            pair[0] !== undefined && pair[1] !== undefined
        ),
        tap(([algorithmCompatibilityInfo, stepCompatibilityInfo]) => {
          this.algorithmSwitcherState.setUsingDeprecated(algorithmCompatibilityInfo.deprecated);
          const compatibilityState = this.checkAlgorithmCompatibilityWithPipeline(
            algorithmCompatibilityInfo,
            stepCompatibilityInfo
          );
          this.algorithmSwitcherState.setCompatibilityState(compatibilityState);
        })
      )
  );

  private readonly clearAlgorithmDetailWhenSelectionCleared = this.algorithmSwitcherState.effect(
    (id$: Observable<string | undefined>) =>
      id$.pipe(
        filter((id) => id == null),
        tap(() => this.algorithmDetailEffects.reset())
      )
  );

  private readonly wireUpFromData = this.algorithmSwitcherState.effect(
    (data$: Observable<SwitchAlgorithmData | undefined>) =>
      data$.pipe(
        filter((d): d is SwitchAlgorithmData => d !== undefined),
        tap((data: SwitchAlgorithmData) => {
          if (data.pipelineStepId) {
            this.pipelineStepDetailEffects.loadStep(data.pipelineStepId);
          }
          this.algorithmDetailEffects.loadAlgorithm(
            this.algorithmToSwitchToId$.pipe(filter((id): id is string => id !== undefined))
          );

          this.algorithmEffects.setEnvironmentFilter(data.algorithmExecutionEnvironment);
          this.algorithmEffects.searchTerm$.pipe(take(1)).subscribe((searchTerm) => {
            this.algorithmSwitcherState.setInitialSearchTerm(searchTerm);
          });
        })
      )
  );

  public readonly cancel = this.algorithmSwitcherState.effect((trigger$: Observable<void>) =>
    trigger$.pipe(tap(() => this.algorithmSwitcherState.cancellation$.next()))
  );

  public readonly accept = this.algorithmSwitcherState.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      concatLatestFrom(() => this.algorithmToSwitchToId$),
      filter((pair): pair is [void, string] => Boolean(pair[1])),
      tap(([, id]) => this.algorithmSwitcherState.acceptance$.next(id))
    )
  );

  public readonly loadMoreAlgorithms = this.algorithmEffects.loadMoreItems;

  // ============================================================================
  // Helpers
  // ============================================================================

  private resolvePipelineName(data: SwitchAlgorithmData, language: string): string {
    if (typeof data.pipelineName === 'string') {
      return data.pipelineName;
    } else {
      return data.pipelineName[language];
    }
  }

  private checkAlgorithmCompatibilityWithPipeline(
    algorithmCompatibilityInfo: AlgorithmCompatibilityInfo,
    stepCompatibilityInfo: PipelineStepCompatibilityInfo
  ): CompatibilityState {
    switch (stepCompatibilityInfo.task) {
      case StepTask.OCR:
        if (algorithmCompatibilityInfo.engine !== Engine.MLKIT) {
          return { kind: 'no_ocr' };
        }
        break;

      case StepTask.MAPPING:
        if (algorithmCompatibilityInfo.engine !== Engine.MAP) {
          return { kind: 'no_mapping' };
        }
        break;

      default: {
        const mismatchedLabels = Array.from(stepCompatibilityInfo.outputLabels)
          .filter((label) => !algorithmCompatibilityInfo.outputLabels.has(label))
          .map((uuid) => ({ uuid, name: stepCompatibilityInfo.outputLabelNames[uuid] ?? { en: uuid } }));
        if (mismatchedLabels.length > 0) {
          return { kind: 'label_mismatch', mismatchedLabels };
        }
        break;
      }
    }

    return { kind: 'compatible' };
  }
}
